/*
Capabilities:
- CAP-MEMBER-ONBOARDING

Responsibilities:
- Render and validate the new-member registration flow for the Telegram web
  app, then exchange the onboarding token for a linked app token.

Context:
- project-intelligence/capabilities/requested/member/member-onboarding.md
*/

import React, { useState, useEffect, useRef } from 'react';
import { isMobile } from 'react-device-detect';
import { useNavigate } from "react-router-dom";

import { useSimpleQuery } from '../../hooks';

import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import objectHash from 'object-hash';

import { HorizontallyCenteringContainer, VerticalSeparator, registerNewMember } from './utils';
import { FullWidthSpinner } from '../loader-components';
import { DEFAULT_BACKGROUND_COLOR } from './utils';

const GET_LOCATION_DURING_REGISTRATION = false;

export function TelegramAppRegistration() {
    /*
    CAP-MEMBER-ONBOARDING entry point for new registration.

    Notable frontend behavior:
    - performs local validation before network submission,
    - stores a hash of the last successful registration payload so the UI can
      recover from an ambiguous retry/response case,
    - redirects into the configured Telegram app route once `app_token` is
      returned.
    */
    const [isSigningUp, setIsSigningUp] = useState(false);
    const [isDoneSigningUp, setIsDoneSigningUp] = useState(false);
    const [registrationToken, setRegistrationToken] = useState(null);
    const [submissionLocation, setSubmissionLocation] = useState({
        submissionLongitude: null,
        submissionLatitude: null,
    });

    const query = useSimpleQuery();
    const navigate = useNavigate();

    const tokenFromQueryParams = query.token || '';
    const tokenFromQueryParamsSplit = tokenFromQueryParams.split(':');
    const validTokenFromQueryParams = tokenFromQueryParamsSplit.length === 3;
    const hasRequestedLocationRef = useRef(false);

    // Set title
    useEffect(() => {
        document.getElementsByTagName('title')[0].innerText = 'Swensen\'s Cool Rewards';
    }, []);

    useEffect(() => {
        if (hasRequestedLocationRef.current) {
            return;
        }
        hasRequestedLocationRef.current = true;

        const reportLocation = ({ submissionLongitude = null, submissionLatitude = null }) => {
            const nextLocation = { submissionLongitude, submissionLatitude };
            setSubmissionLocation(nextLocation);
        };

        if (!GET_LOCATION_DURING_REGISTRATION || !navigator?.geolocation) {
            reportLocation({});
            return;
        } else {
            navigator.geolocation.getCurrentPosition(
                ({ coords }) => reportLocation({
                    submissionLongitude: coords?.longitude ?? null,
                    submissionLatitude: coords?.latitude ?? null,
                }),
                () => reportLocation({}),
                {
                    enableHighAccuracy: true,
                    timeout: 5000,
                    maximumAge: 0,
                }
            );
        }
    }, []);

    // Redirect after grabbing token from URL (if available)
    useEffect(() => {
        if (validTokenFromQueryParams) {
            setTimeout(() => {
                setRegistrationToken(tokenFromQueryParams);
            }, 1000)
        }
    }, [validTokenFromQueryParams, tokenFromQueryParams]);

    const nameEl = useRef(null);
    const emailEl = useRef(null);
    const mobileNumberEl = useRef(null);
    const dateOfBirthEl = useRef(null);
    const allowSMSCheckbox = useRef(null);
    const allowEmailCheckbox = useRef(null);
    const allowInstantMessengerServicesCheckbox = useRef(null);
    const cfmInfoRealCheckbox = useRef(null);
    const cfmAcceptTnCCheckbox = useRef(null);

    const [overallErrorText, setOverallErrorText] = useState(null);

    const [nameErrorText, setNameErrorText] = useState(null);
    const [emailErrorTextLine1, setEmailErrorTextLine1] = useState(null);
    const [emailErrorTextLine2, setEmailErrorTextLine2] = useState(null);
    const [mobileNumberErrorTextLine1, setMobileNumberErrorTextLine1] = useState(null);
    const [mobileNumberErrorTextLine2, setMobileNumberErrorTextLine2] = useState(null);
    const [dateOfBirthErrorText, setDateOfBirthErrorText] = useState(null);
    const [cfmInfoRealErrorText, setCfmInfoRealErrorText] = useState(null);
    const [cfmAcceptTnCErrorText, setCfmAcceptTnCErrorText] = useState(null);

    const dataElements = {
        name: { el: nameEl, getValue: () => nameEl?.current?.value && nameEl?.current?.value.trim(), setError: setNameErrorText },
        email: { el: emailEl, getValue: () => emailEl?.current?.value && emailEl?.current?.value.trim(), setError: (l1, l2) => { setEmailErrorTextLine1(l1); setEmailErrorTextLine2(l2); } },
        mobileNumber: { el: mobileNumberEl, getValue: () => mobileNumberEl?.current?.value && mobileNumberEl?.current?.value.trim(), setError: (l1, l2) => { setMobileNumberErrorTextLine1(l1); setMobileNumberErrorTextLine2(l2); } },
        dateOfBirth: { el: dateOfBirthEl, getValue: () => dateOfBirthEl?.current?.value && dateOfBirthEl?.current?.value.trim(), setError: setDateOfBirthErrorText },
        allowSMS: { el: allowSMSCheckbox, getValue: () => allowSMSCheckbox?.current?.checked, setError: null },
        allowEmail: { el: allowEmailCheckbox, getValue: () => allowEmailCheckbox?.current?.checked, setError: null },
        allowInstantMessengerServices: { el: allowInstantMessengerServicesCheckbox, getValue: () => allowInstantMessengerServicesCheckbox?.current?.checked, setError: null },
        cfmInfoReal: { el: cfmInfoRealCheckbox, getValue: () => cfmInfoRealCheckbox?.current?.checked, setError: setCfmInfoRealErrorText },
        cfmAcceptTnC: { el: cfmAcceptTnCCheckbox, getValue: () => cfmAcceptTnCCheckbox?.current?.checked, setError: setCfmAcceptTnCErrorText },
    }

    function getData({ validateItem = null } = {}) {
        const data = Object.fromEntries(
            Object.keys(dataElements).map(k => {
                return [k, dataElements[k].getValue()]
            })
        );
        data.token = registrationToken;
        let numErrors = 0;

        if (validateItem === 'name' || validateItem === 'all') {
            if(!data.name.trim()) {
                dataElements.name.setError('Please enter your name.')
                numErrors++;
            } else {
                dataElements.name.setError(null);
            }
        }

        if (validateItem === 'email' || validateItem === 'all') {
            // eslint-disable-next-line no-control-regex
            const re = new RegExp(/^(?:[a-z0-9+!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/i);
            if(!re.test(data.email)) {
                dataElements.email.setError('Please enter a valid email address.', '(e.g.: bob@gmail.com)')
                numErrors++;
            } else {
                dataElements.email.setError(null);
            }
        }

        if (validateItem === 'mobileNumber' || validateItem === 'all') {
            // const re = new RegExp(/^(\+[0-9]+\s{0,1}|)[0-9]{8,}$/);
            const re = new RegExp(/^[0-9]{8}$/);
            if(!re.test(data.mobileNumber)) {
                dataElements.mobileNumber.setError('Please enter a valid mobile number.', '(e.g.: 91234567)')
                numErrors++;
            } else {
                dataElements.mobileNumber.setError(null, null);
            }
        }

        if (validateItem === 'dateOfBirth' || validateItem === 'all') {
            if(!data.dateOfBirth || !Number.isFinite((new Date(data.dateOfBirth)).getTime()) || !((new Date(data.dateOfBirth)).getTime() >= -2209013725000) || !((new Date(data.dateOfBirth)) < new Date())) {
                dataElements.dateOfBirth.setError('Please enter a valid date of birth.')
                numErrors++;
            } else {
                dataElements.dateOfBirth.setError(null);
                data.dateOfBirth = (new Date(data.dateOfBirth)).getTime() - 60 * 60 * 1000 * 8
            }
        }

        if (validateItem === 'cfmInfoReal' || validateItem === 'all') {
            if(!data.cfmInfoReal) {
                dataElements.cfmInfoReal.setError(true)
                numErrors++;
            } else {
                dataElements.cfmInfoReal.setError(null);
            }
        }

        if (validateItem === 'cfmAcceptTnC' || validateItem === 'all') {
            if(!data.cfmAcceptTnC) {
                dataElements.cfmAcceptTnC.setError(true)
                numErrors++;
            } else {
                dataElements.cfmAcceptTnC.setError(null);
            }
        }

        return { data, numErrors };
    }

    if (!registrationToken) {
        return (
            <FullWidthSpinner
                text='Loading...'
                heightInVH={isMobile ? 74 : 100}
                scale={3} />
        );
    }

    if (isDoneSigningUp) {
        return (
            <FullWidthSpinner
                text='Sign Up Complete. Redirecting...'
                heightInVH={isMobile ? 64 : 90}
                scale={3} />
        );
    }

    return (
        <div>
            <HorizontallyCenteringContainer>
                <div style={{
                    position:'absolute', top: '0px', zIndex: '-1',
                    width: '100%',
                    height: 'calc(20px)',
                    // height: 'calc(20px + 36px + 20px)',
                    backgroundColor: 'var(--app-top-strip-color)'
                    // backgroundColor: 'var(--swensens-brand-color)'
                }}/>
                <div style={{
                    position:'absolute', top: '0px', zIndex: '-2',
                    width: '100%', height: '100vh',
                    backgroundColor: DEFAULT_BACKGROUND_COLOR,
                }}/>

                {
                    isSigningUp && (
                        <FullWidthSpinner
                            text='Signing Up...'
                            heightInVH={isMobile ? 64 : 90}
                            scale={3} />
                    )
                }

                <div style={{ width: '360px', paddingLeft: '10px', paddingRight: '10px', display: isSigningUp ? 'none' : '' }}>
                    <div style={{ width: '340px' }}>
                    <div style={{
                        height: 'calc(20px)',
                        backgroundColor: 'var(--app-top-strip-color)'
                    }}/>
                        <VerticalSeparator height='0.5em'/>
                        <h4 className="bold">Sign Up for Cool Rewards Membership</h4>
                        <hr/>
                        <div style={{ display: isSigningUp ? 'none' : '' }}>
                            <p className="app-text thin-line">You are one step away from enjoying our Cool Rewards membership benefits. Complete all fields below and you are ready to go.</p>
                            <VerticalSeparator/>
                            {
                                overallErrorText && (
                                    <>
                                        <HorizontallyCenteringContainer>
                                            <div className="input-error">{overallErrorText}</div>
                                        </HorizontallyCenteringContainer>
                                        <VerticalSeparator/>
                                    </>
                                )
                            }

                            <div>Name (required):</div>
                            <HorizontallyCenteringContainer>
                                <input ref={nameEl} className="underline-input" onChange={() => getData({ validateItem: 'name' })}/>
                            </HorizontallyCenteringContainer>
                            { nameErrorText && <div className="input-error">{nameErrorText}</div> }
                            <VerticalSeparator />

                            <div>Email (required):</div>
                            <HorizontallyCenteringContainer>
                                <input ref={emailEl} className="underline-input" onChange={() => getData({ validateItem: 'email' })}/>
                            </HorizontallyCenteringContainer>
                            { emailErrorTextLine1 && <div className="input-error">{emailErrorTextLine1}</div> }
                            { emailErrorTextLine2 && <div className="input-error">{emailErrorTextLine2}</div> }
                            <VerticalSeparator />

                            <div>Mobile Number (required):</div>
                            <HorizontallyCenteringContainer>
                                <input ref={mobileNumberEl} type="tel" pattern="[0-9]{8}" className="underline-input" onChange={() => getData({ validateItem: 'mobileNumber' })}/>
                            </HorizontallyCenteringContainer>
                            { mobileNumberErrorTextLine1 && <div className="input-error">{mobileNumberErrorTextLine1}</div> }
                            { mobileNumberErrorTextLine2 && <div className="input-error">{mobileNumberErrorTextLine2}</div> }
                            <VerticalSeparator />

                            <div>Date of Birth (required):</div>
                            <HorizontallyCenteringContainer>
                                <input ref={dateOfBirthEl} type="date" className="underline-input" onChange={() => getData({ validateItem: 'dateOfBirth' })}/>
                            </HorizontallyCenteringContainer>
                            { dateOfBirthErrorText && <div className="input-error">{dateOfBirthErrorText}</div> }
                            <VerticalSeparator />

                            <div style={{ fontSize: '0.8em' }}>
                                <div>Entering your Date of Birth allows us to provide you with birthday privileges.</div>
                                <VerticalSeparator/>
                                <div>Get updated on Cool Rewards Members' privileges and promotions via the following:</div>
                                <VerticalSeparator/>
                                <div className="horizontal-flex">
                                    <Form.Check ref={allowSMSCheckbox} className="fixed-size" inline type='checkbox' checked onChange={() => getData({ validateItem: 'allowSMS' })}/>
                                    <div>SMS</div>
                                </div>
                                <VerticalSeparator height='0.25em'/>
                                <div className="horizontal-flex">
                                    <Form.Check ref={allowEmailCheckbox} className="fixed-size" inline type='checkbox' checked onChange={() => getData({ validateItem: 'allowEmail' })}/>
                                    <div>Email</div>
                                </div>
                                <VerticalSeparator height='0.25em'/>
                                <div className="horizontal-flex">
                                    <Form.Check ref={allowInstantMessengerServicesCheckbox} className="fixed-size" inline type='checkbox' checked onChange={() => getData({ validateItem: 'allowInstantMessengerServices' })}/>
                                    <div>Instant Messenger Services (e.g.: Telegram)</div>
                                </div>
                                <VerticalSeparator height='1em'/>
                                <div className="horizontal-flex">
                                    <Form.Check ref={cfmInfoRealCheckbox} className="fixed-size" inline type='checkbox' onChange={() => getData({ validateItem: 'cfmInfoReal' })}/>
                                    <div>I confirm the above information provided is genuine.</div>
                                </div>
                                { cfmInfoRealErrorText && <div className="input-error">Please confirm that the above information provided is genuine.</div> }
                                <VerticalSeparator height='0.25em'/>
                                <div className="horizontal-flex">
                                    <Form.Check ref={cfmAcceptTnCCheckbox} className="fixed-size" inline type='checkbox' onChange={() => getData({ validateItem: 'cfmAcceptTnC' })}/>
                                    <div>I agree to the <a href="https://swensens.com.sg/membership-terms-conditions/" target="_blank" rel="noreferrer">Terms & Conditions</a> and <a href="https://swensens.com.sg/privacy-policy/" target="_blank" rel="noreferrer">Privacy Policy</a> of Swensens's Cool Rewards Membership Program</div>
                                </div>
                                { cfmAcceptTnCErrorText && <div className="input-error">Please review the policies and accept them to continue.</div> }
                            </div>


                            <VerticalSeparator/>
                            <HorizontallyCenteringContainer>
                                <Button
                                    className="w80 swensens-button"
                                    onClick={() => {
                                        const { data, numErrors } = getData({ validateItem: 'all' });
                                        if (numErrors === 0) {
                                            const submissionData = {
                                                ...data,
                                                ...submissionLocation,
                                            };
                                            setOverallErrorText(null)
                                            console.log('Submitting:', submissionData)
                                            setIsSigningUp(true);
                                            
                                            const dataCopy = Object.assign({}, submissionData);
                                            delete dataCopy.allowEmail;
                                            delete dataCopy.allowSMS;
                                            delete dataCopy.allowInstantMessengerServices;
                                            const registrationHash = objectHash(dataCopy);

                                            registerNewMember(submissionData)
                                                .then(({ data: { success, message, app_token: appToken } }) => {
                                                    if (success) {
                                                        localStorage.setItem('last-successful-registration', JSON.stringify({
                                                            registrationHash, data: submissionData, appToken
                                                        }))
                                                        navigate(`/telegram-app?token=${appToken}`)
                                                        setIsDoneSigningUp(true);
                                                    } else {
                                                        let lastSuccessfulRegistration;
                                                        try {
                                                            lastSuccessfulRegistration = JSON.parse(localStorage.getItem('last-successful-registration') || 'null')
                                                        } catch {
                                                            lastSuccessfulRegistration = null;
                                                        }
                                                        if (!!lastSuccessfulRegistration && lastSuccessfulRegistration.registrationHash === registrationHash) {
                                                            const appToken = lastSuccessfulRegistration.appToken; 
                                                            navigate(`/telegram-app?token=${appToken}`)
                                                            setIsDoneSigningUp(true);
                                                        } else {
                                                            setOverallErrorText(`Error Signing Up. (${message})`);
                                                        }
                                                    }
                                                })
                                                .catch(e => {
                                                    setOverallErrorText(`Error Signing Up. Try again later.`);
                                                })
                                                .then(() => {
                                                    setIsSigningUp(false);
                                                })
                                        }
                                    }}
                                >Sign Up</Button>
                            </HorizontallyCenteringContainer>
                            <HorizontallyCenteringContainer>
                                { overallErrorText && <div className="input-error">{overallErrorText}</div> }
                            </HorizontallyCenteringContainer>
                        </div>
                        <VerticalSeparator height='1.5em'/>
                    </div>
                </div>

            </HorizontallyCenteringContainer>
            <VerticalSeparator height='2em'/>
        </div>
    );
}
